import { FactCheck, HelpOutline } from "@mui/icons-material";
import {
  Box,
  Button,
  Grid,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useSnackbar } from "notistack";
import { useCallback, useEffect, useState } from "react";
import checkFasta from "../../utils/fastaValidator";
import DropZone from "../DropZone/DropZone";
import useDropZoneHelper from "../DropZone/useDropZoneHelper";

export default function FastaForm({
  fasta,
  setFasta,
  type,
  annotation = false,
  setInputFile = null,
  molecule = false,
}) {
  const [exampleData, setExampleData] = useState("loading");
  const [file, setFile] = useState(null);

  const { enqueueSnackbar } = useSnackbar();
  const { files, resetDropZone, dropZoneHelper } = useDropZoneHelper();

  useEffect(() => {
    setFile(files.length > 0 ? files[0] : null);
  }, [files, setFile]);
  const [exampleTips, setExampleTips] = useState(
    "Please input data with the same format as example data(Every sequence must be continual without blank space and defferent rows situation), and the sequences have to be legal component (LAGVESIKRDTPNQFYMHCWXUBZO)"
  );

  useEffect(() => {
    if (file) {
      if (file.size < 1000000) {
        readFileAsSeq(file).then((res) => {
          setFasta(res);
        });
        setInputFile(null);
      } else {
        setInputFile(file);
        enqueueSnackbar(
          "Considering browsers are not able to check such large file, it will be checked after submitted",
          { variant: "info" }
        );
      }
    }
  }, [file, setFasta]);

  useEffect(() => {
    if (!fasta) resetDropZone();
  }, [fasta, resetDropZone]);

  useEffect(() => {
    if (molecule) {
      setExampleTips("Please input data with the same format as example data");
      setExampleData("CC12CC1(CO2)C#C\nCC(=O)C1CCC2CC12\nCC1CC2NC2C1(C)O");
    } else {
      if (type === "Detectability") {
        setExampleData(
          ">1\nLLSEVEELNMSLTALREK\n>2\nERMDEEQKLYTD\n>3\nYVPRAVLVDLEPGTMDSIR\n>4\nTAHYGSLPQKSHGR"
        );
      } else {
        setExampleData(
          ">1a81_A\nSANHLPFFFGNITREEAEDYLVQGGMSDGLYLLRQSRNYLGGFALSVAHGRKAHHYTIERELNGTYAIAGGRTHASPADLCHYHSQESDGLVCLLKKPFNRPQGVQPKTGPFEDLKENLIREYVKQTWNLQGQALEQAIISQKPQLEKLIATTAHEKMPWFHGKISREESEQIVLIGSKTNGKFLIRARDNNGSYALCLLHEGKVLHYRIDKDKTGKLSIPEGKKFDTLWQLVEHYSYKADGLLRVLTVPCQKI"
        );
      }
    }

    // fetch("protTestData.txt")
    //   .then((res) => res.text())
    //   .then(setExampleData);
  }, []);

  const checkDataset = useCallback(async () => {
    return checkFasta(fasta, enqueueSnackbar, type, annotation);
  }, [fasta, enqueueSnackbar]);

  return (
    <Grid container spacing={4} padding={2}>
      <Grid item xs={12} sm={12} md={6}>
        <Typography variant="h6">
          {molecule
            ? "Enter the SMILES sequences and every row includes one SMILES sequence:"
            : "Enter the query protein sequence with FASTA format sequences in training and testing dataset with FASTA format:"}

          <Tooltip title={exampleTips} placement="top">
            <HelpOutline sx={{ float: "right" }} />
          </Tooltip>
        </Typography>
        <TextField
          value={fasta}
          onChange={({ target: { value } }) => {
            setFasta(value);
          }}
          variant="outlined"
          label={molecule ? "Input SMILES data" : "Input dataset"}
          multiline
          fullWidth
          rows={4}
          sx={{ margin: "20px 0" }}
        />
        {annotation && type === "protein" ? (
          <Box
            textAlign={"justify"}
            fontSize={"small"}
            margin={"10px"}
            color="#717171e0"
            fontWeight={"bold"}
          >
            Please input and ensure one sequence with the same format as example
            data (The title consists of PDB id and chain id, such as {">1a81_A"}
            ), while single sequence must be continual without blank space and
            line breaking.
          </Box>
        ) : (
          ""
        )}

        {annotation && (type === "DNA" || type === "RNA") ? (
          <Box
            textAlign={"justify"}
            fontSize={"small"}
            margin={"10px"}
            color="#717171e0"
            fontWeight={"bold"}
          >
            Please input data with the same format as example data, while single
            sequence must be continual without blank space and line breaking.
          </Box>
        ) : (
          ""
        )}
        {!annotation ? (
          <Box
            textAlign={"justify"}
            fontSize={"small"}
            margin={"0 10px 20px 10px"}
            color="#717171e0"
            fontWeight={"bold"}
          >
            Please input data with the same format as example data (The title
            consists of id | label | [training/testing] ('training' indicates
            training set, 'testing' indicates testing set, and both of two sets
            must be included in the input data), while single sequence must be
            continual without blank space and line breaking).
          </Box>
        ) : (
          ""
        )}
        <Box>
          <Button
            onClick={() => setFasta(exampleData)}
            variant="contained"
            color="secondary"
          >
            Example
          </Button>
          <Button onClick={() => setFasta("")} sx={{ ml: 1 }}>
            clear
          </Button>
          <Button
            onClick={checkDataset}
            disabled={!fasta.length && !file}
            sx={{ ml: 1 }}
            color="primary"
            variant="contained"
          >
            <FactCheck />
            check format
          </Button>
        </Box>
      </Grid>
      <Grid item xs={12} sm={12} md={6}>
        <Typography variant="h6">
          Or upload your training and testing dataset:
        </Typography>
        <DropZone
          helper={dropZoneHelper}
          maxFiles={1}
          validator={fileValidator}
        >
          Drag and drop dataset or click to select FASTA file to upload
        </DropZone>
      </Grid>
    </Grid>
  );
}

const readFileAsSeq = (file) =>
  new Promise((res, rej) => {
    const reader = new FileReader();

    reader.onload = () => res(reader.result);
    reader.onerror = () => rej(reader.error);
    reader.readAsText(file, "utf-8");
  });

const fileValidator = (file) => {
  if (file.name.substr(-5) !== "fasta") {
    if (file.name.substr(-10) !== ".fasta .fa") {
      if (file.name.substr(-4) !== ".fna") {
        return {
          message: "please select file with .fasta extension",
        };
      }
    }
  }

  return null;
};
